import { useState } from 'react';
import { Box, Stack, Typography, Link, Container, TextField, Button, CircularProgress } from '@mui/material';
import TwitterIcon from '@/assets/images/twitter-icon.svg';
import LinkedInIcon from '@/assets/images/linkedin-icon.svg';
import InstagramIcon from '@/assets/images/instagram-icon.svg';
import navLogo from '@/assets/images/logo.svg';
import { NavLink } from 'react-router-dom';

const Footer = ({
  subscribeToNotifications,
  subscriptionStatus,
}: {
  subscribeToNotifications: (email: { EmailId: string }) => void;
  subscriptionStatus: string;
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubscribe = async () => {
    if (validateEmail(email)) {
      setLoading(true);
      setError('');

      try {
        await subscribeToNotifications({ EmailId: email });
        setEmail('');
      } catch {
        setError('Subscription failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setError('Please enter a valid email address');
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'primary.main',
        py: 1.5,
        color: 'white',
        boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center" justifyContent="space-between">
          {/* Logo & Copyright */}
          <Box width={{ xs: '100%', md: 'unset' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', my: 3 }}>
              <img src={navLogo} alt="Logo" style={{ height: 55 }} />
            </Box>
            <Typography variant="body2" sx={{ fontSize: 13, mb: { xs: 0, md: 3 }, fontWeight: 500 }}>
              © {new Date().getFullYear()} Epic Advanced Materials, LLC. All Rights Reserved.
            </Typography>
          </Box>

          {/* Subscription Form */}
          <Box width={{ xs: '100%', md: 'unset' }}>
            <Typography variant="caption" display="block" mb={1} sx={{ fontSize: 13 }}>
              Stay Up to Date
            </Typography>
            <Box display="flex" alignItems="center">
              <TextField
                placeholder="Email Address"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  width: '100%',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  '& .MuiInputBase-input': {
                    height: '20px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                  },
                  '& .MuiOutlinedInput-root': {
                    minHeight: '37px',
                    fontSize: '16px !important',
                    '& fieldset': {
                      borderRight: 'none',
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                  },
                }}
                error={!!error}
              />
              <Button
                sx={{
                  backgroundColor: 'custom.orange',
                  px: '30px',
                  py: '7px',
                  ml: '-6px',
                  '&:hover': { backgroundColor: 'custom.orange' },
                  height: '37.5px !important',
                  width: '105px',
                }}
                variant="contained"
                size="small"
                onClick={handleSubscribe}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Subscribe'}
              </Button>
            </Box>
            {error && (
              <Typography variant="caption" color="error" mt={1}>
                {error}
              </Typography>
            )}
            {subscriptionStatus && (
              <Typography variant="caption" color="success.main" mt={1}>
                {subscriptionStatus}
              </Typography>
            )}
          </Box>

          {/* Privacy and Terms Links */}
          <Stack direction="row" spacing={5} justifyContent="center" alignItems="center">
            <Typography
              variant="body2"
              component={NavLink}
              to="/privacy_policy"
              sx={{ color: 'white', fontSize: 13, fontWeight: '500', textDecoration: 'none' }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="body2"
              component={NavLink}
              to="/terms_conditions"
              sx={{ color: 'white', fontSize: 13, fontWeight: '500', textDecoration: 'none' }}
            >
              Terms & Conditions
            </Typography>
          </Stack>

          {/* Social Media Icons */}
          <Stack direction="row" spacing={3} justifyContent="center" alignItems="center">
            <Link href="https://x.com/EpicNanotech" target="_blank" rel="preload noopener noreferrer">
              <img src={TwitterIcon} alt="twitter" width={24} height={24} />
            </Link>
            <Link
              href="https://www.linkedin.com/company/epic-advanced-materials/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={LinkedInIcon} alt="linkedin" width={24} height={24} />
            </Link>
            <Link href="https://www.instagram.com/epicnanotech/" target="_blank" rel="preload noopener noreferrer">
              <img src={InstagramIcon} alt="instagram" width={24} height={24} />
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
